import React, { useState, useEffect, useCallback } from "react";
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
} from "firebase/auth";
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Typography, MenuItem, Select, Box, Snackbar, Alert, CircularProgress, TablePagination, TableSortLabel } from "@mui/material";
import { doc, setDoc, collection, getDocs, onSnapshot, deleteDoc } from "firebase/firestore";
import { auth, db } from "../../firebaseConfig";
import ConfirmDialog from "../../utils/ConfirmDialog";

const AllStudentsData = () => {
    const [sortConfig, setSortConfig] = useState({ key: "date", direction: "asc" });
    const [students, setStudents] = useState([]);
    const [open, setOpen] = useState(false);
    const [selectedStudentId, setSelectedStudentId] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        mobile: "",
        email: "",
        gender: "",
        dob: "",
    });
    const [selectedRecordId, setSelectedRecordId] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [videoTree, setVideoTree] = useState([]);

    useEffect(() => {
        fetchStudents();
    }, []);

    useEffect(() => {
        const unsubscribeStudents = onSnapshot(collection(db, "users"), (snapshot) => {
            const studentList = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })).filter((user) => user.role === "student");
            setStudents(studentList);
        });

        return () => {
            unsubscribeStudents();
        };
    }, []);

    useEffect(() => {
        localStorage.setItem("studentDataCount", students.length);
    }, [students])

    const fetchVideoStructure = async (path = "root", pathArray = []) => {
        const refPath = `videos/${path}/items`;
        const snapshot = await getDocs(collection(db, refPath));

        console.log("Fetching:", refPath, "| Docs count:", snapshot.size);
        
        const results = [];
      
        for (const docSnap of snapshot.docs) {
          const data = docSnap.data();
          const itemPath = [...pathArray, data.name];
      
          if (data.type === "folder") {
            console.log("Recursing into folder:", data.name, "with path:", `${path}/items/${data.name}`);
            const children = await fetchVideoStructure(`${path}/items/${data.name}`, itemPath);
            results.push({
              id: docSnap.id,
              name: data.name,
              type: "folder",
              path: itemPath,
              children,
            });
          } else {
            results.push({
              id: docSnap.id,
              name: data.name,
              type: "video",
              path: itemPath,
              url: data.url,
            });
          }
        }
      
        return results;
      };

      useEffect(() => {
        const loadVideoStructure = async () => {
          const data = await fetchVideoStructure();
          setVideoTree(data);  // Define this with useState
        };
        loadVideoStructure();
      }, []);

    // ✅ Fetch Students List
    const fetchStudents = async () => {
        setLoading(true);
        try {
            const querySnapshot = await getDocs(collection(db, "users"));
            const studentList = querySnapshot.docs
                .map((doc) => ({ id: doc.id, ...doc.data() }))
                .filter((user) => user.role === "student"); // Filter only students
            setStudents(studentList);
        } catch (error) {
            console.error("Error fetching students:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    const sortedStudentRecords = [...students].sort((a, b) => {
        if (sortConfig.key === "date") {
            return sortConfig.direction === "desc"
                ? new Date(a.date) - new Date(b.date)
                : new Date(b.date) - new Date(a.date);
        }
        return sortConfig.direction === "asc"
            ? a[sortConfig.key]?.localeCompare(b[sortConfig.key])
            : b[sortConfig.key]?.localeCompare(a[sortConfig.key]);
    });

    // ✅ Handle Form Changes
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const getAdminCredentials = () => {
        return {
            email: localStorage.getItem("adminEmail"),
            password: localStorage.getItem("adminPassword"),
        };
    };


    // ✅ Create New Student with Default Password
    const handleCreateStudent = async () => {
        const adminUser = auth.currentUser; // Get the current admin session
        if (!adminUser) {
            console.error("No admin user found.");
            return;
        }

        const { email: adminEmail, password: adminPassword } = getAdminCredentials();
        if (!adminEmail || !adminPassword) {
            console.error("Admin credentials not found. Please log in again.");
            return;
        }

        const defaultPassword = "Student@123"; // Default password for students
        try {
            // Step 1: Create new student account (this auto-signs in the student)
            const userCredential = await createUserWithEmailAndPassword(auth, formData.email, defaultPassword);

            // Step 2: Store student details in Firestore
            await setDoc(doc(db, "users", userCredential.user.uid), {
                ...formData,
                role: "student",
            });

            handleClose();
            setSnackbar({ open: true, message: "Student created successfully!", severity: "success" });

            // Step 3: Log out the newly created student
            await signOut(auth);

            // Step 4: Log the admin back in automatically
            await signInWithEmailAndPassword(auth, adminEmail, adminPassword);

            setMessage("Student account created successfully!");
            setFormData({ name: "", mobile: "", email: "", gender: "", dob: "" });

            // Step 5: Refresh student and HR records
            await fetchStudents();
        } catch (err) {
            console.error("Error creating student:", err);
            setMessage("Error creating student account.");
            setSnackbar({ open: true, message: "Error creating student account.", severity: "error" });
        }
    };

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setMessage("");
    };

    const handleStudentDelete = useCallback((studentId) => {
        setSelectedStudentId(studentId);
        setConfirmOpen(true);
    }, []);

    const handleStudentConfirmClose = useCallback(() => {
        setConfirmOpen(false);
        setSelectedStudentId(null);
    }, []);

    const confirmDeleteStudent = async () => {
        try {
            // ✅ Delete student from "users" collection
            await deleteDoc(doc(db, "users", selectedStudentId));

            // ✅ Update local student list
            setStudents(prev => prev.filter(student => student.id !== selectedStudentId));

            // ✅ Success message
            setSnackbar({
                open: true,
                message: "Student deleted successfully!",
                severity: "success",
            });
        } catch (error) {
            console.error("Error deleting student:", error);
            setSnackbar({
                open: true,
                message: "Failed to delete student!",
                severity: "error",
            });
        } finally {
            handleStudentConfirmClose();
        }
    };

    const handleConfirmClose = useCallback(() => {
        setConfirmOpen(false);
        setSelectedRecordId(null);
    }, []);

    const paginatedStudentRecords = sortedStudentRecords.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const customStudentCellStyle = {
        fontSize: '12px'
    }

    const customHeaderCellStyle = {
        fontSize: '13px'
    }

    return (
        <div style={{ padding: "20px" }}>
            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <h3 style={{ color: '#9c27b0', margin: 0 }}>Students List</h3>
                <Button variant="contained" color="primary" onClick={handleOpen}>
                    Create New Student
                </Button>
            </Box>

            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" mt={5}>
                    <CircularProgress />
                </Box>) :

                (<TableContainer component={Paper} sx={{ mt: 3 }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                                <TableCell style={customHeaderCellStyle}><TableSortLabel
                                        active={sortConfig.key === "name"}
                                        direction={sortConfig.direction}
                                        onClick={() => handleSort("name")}
                                    ><strong>Name</strong>
                                    </TableSortLabel>
                                    </TableCell>
                                <TableCell style={customHeaderCellStyle}><strong>Mobile Number</strong></TableCell>
                                <TableCell style={customHeaderCellStyle}><strong>Email</strong></TableCell>
                                <TableCell style={customHeaderCellStyle}><strong>Gender</strong></TableCell>
                                <TableCell style={customHeaderCellStyle}><strong>Date of Birth</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedStudentRecords.map((student) => (
                                <TableRow key={student.id} sx={{
                                    position: 'relative',
                                    '&:hover': {
                                        backgroundColor: '#f8fafd',
                                        border: '1px solid #1976d2',
                                        boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
                                    },
                                    '&:hover .delete-button': {
                                        opacity: 1,
                                    },
                                }}>
                                    <TableCell style={customStudentCellStyle}>{student.name}</TableCell>
                                    <TableCell style={customStudentCellStyle}>{student.mobile}</TableCell>
                                    <TableCell style={customStudentCellStyle}>{student.email}</TableCell>
                                    <TableCell style={customStudentCellStyle}>{student.gender}</TableCell>
                                    <TableCell style={customStudentCellStyle}>{student.dob}</TableCell>
                                    {/* Delete Button */}
                                    <Box
                                        className="delete-button"
                                        sx={{
                                            position: 'absolute',
                                            right: 16,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            opacity: 0,
                                            transition: 'opacity 0.3s',
                                            cursor: 'pointer',
                                            zIndex: 1,
                                        }}
                                        onClick={() => handleStudentDelete(student.id)}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="error"
                                            fontWeight="bold"
                                            sx={{
                                                backgroundColor: '#9c27b0',
                                                padding: '4px 10px',
                                                borderRadius: '4px',
                                                color: '#fff',
                                                boxShadow: 1,
                                                '&:hover': {
                                                    backgroundColor: '#ff6666',
                                                },
                                            }}
                                        >
                                            Delete
                                        </Typography>
                                    </Box>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {/* Pagination */}
                    <TablePagination
                        component="div"
                        count={sortedStudentRecords.length}
                        page={page}
                        onPageChange={(event, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(event) => {
                            setRowsPerPage(parseInt(event.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                    />
                </TableContainer>)}

            {/* ✅ Create Student Dialog */}
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle color="secondary">Create New Student</DialogTitle>
                <DialogContent>
                    <TextField label="Name" name="name" fullWidth margin="normal" onChange={handleChange} value={formData.name} />
                    <TextField label="Mobile Number" name="mobile" fullWidth margin="normal" onChange={handleChange} value={formData.mobile} />
                    <TextField label="Email" name="email" fullWidth margin="normal" onChange={handleChange} value={formData.email} />
                    <Select name="gender" fullWidth margin="normal" value={formData.gender} onChange={handleChange}>
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                    </Select>
                    <TextField label="Date of Birth" type="date" name="dob" fullWidth margin="normal" onChange={handleChange} value={formData.dob} InputLabelProps={{ shrink: true }} />
                    {message && <p>{message}</p>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="secondary">Cancel</Button>
                    <Button onClick={handleCreateStudent} color="primary">Create</Button>
                </DialogActions>
            </Dialog>

            {/* Confirm Dialog */}
            <ConfirmDialog
                open={confirmOpen}
                title="Confirm Deletion"
                content={`Are you sure you want to delete this Student ?`}
                onClose={handleConfirmClose}
                onConfirm={confirmDeleteStudent}
            />

        </div>
    )
}

export default AllStudentsData